import React, { useState, useEffect, useCallback } from 'react';
import { X, Printer } from 'lucide-react';

// Attendance summary over a date range — printable.
const AttendanceReportModal = ({ apiCall, year, className, sectionCode, onClose, showError }) => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [rows, setRows] = useState([]);

  const load = useCallback(async () => {
    try {
      let url = `/academic/attendance/summary?academicYear=${year}`;
      if (className) url += `&className=${encodeURIComponent(className)}`;
      if (sectionCode) url += `&sectionCode=${encodeURIComponent(sectionCode)}`;
      if (from) url += `&from=${from}`;
      if (to) url += `&to=${to}`;
      setRows(await apiCall(url));
    } catch (err) { showError(err.message); }
  }, [apiCall, year, className, sectionCode, from, to, showError]);

  useEffect(() => { load(); }, [load]);

  const handlePrint = () => {
    const w = window.open('', '', 'width=900,height=700');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Attendance ${className || ''} ${sectionCode || ''} ${year}</title>
      <style>@page{size:A4;margin:1.5cm}body{font-family:Arial;font-size:12px;color:#111}
      h2{margin-bottom:2px}p{color:#555;margin:0 0 12px 0}
      table{width:100%;border-collapse:collapse}td,th{border:1px solid #999;padding:5px 8px;text-align:left}
      th{background:#f0f0f0}td.num{text-align:center}</style></head><body>
      <h2>Attendance Summary — ${className || 'All classes'} ${sectionCode || ''} · ${year}</h2>
      <p>${from || 'start of year'} → ${to || 'today'} · generated ${new Date().toLocaleDateString()}</p>
      <table><thead><tr><th>Student</th><th>Class</th><th>Section</th><th>Absences</th><th>Lates</th><th>Excused</th></tr></thead>
      <tbody>${rows.map(r => `<tr><td>${r.LastName} ${r.FirstName}</td><td>${r.Class}</td><td>${r.Section || ''}</td>
        <td class="num">${r.Absences}</td><td class="num">${r.Lates}</td><td class="num">${r.Excused}</td></tr>`).join('')}
      </tbody></table></body></html>`);
    w.document.close(); w.focus();
    setTimeout(() => w.print(), 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-semibold">Attendance Summary — {className} {sectionCode} · {year}</h3>
          <div className="flex gap-2">
            <button onClick={handlePrint} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1">
              <Printer size={14} /> Print
            </button>
            <button onClick={onClose}><X size={20} /></button>
          </div>
        </div>
        <div className="p-4 flex gap-2 items-end border-b">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <span className="text-xs text-gray-400 pb-2">empty = whole year</span>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-2 py-1.5 text-left text-xs text-gray-500 uppercase">Student</th>
                <th className="px-2 py-1.5 text-center text-xs text-gray-500 uppercase">Absences</th>
                <th className="px-2 py-1.5 text-center text-xs text-gray-500 uppercase">Lates</th>
                <th className="px-2 py-1.5 text-center text-xs text-gray-500 uppercase">Excused</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map(r => (
                <tr key={r.StudentID}>
                  <td className="px-2 py-1.5">{r.LastName} {r.FirstName} <span className="text-xs text-gray-400">{r.Section}</span></td>
                  <td className={`px-2 py-1.5 text-center ${r.Absences > 0 ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>{r.Absences}</td>
                  <td className={`px-2 py-1.5 text-center ${r.Lates > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>{r.Lates}</td>
                  <td className={`px-2 py-1.5 text-center ${r.Excused > 0 ? 'text-blue-600 font-semibold' : 'text-gray-400'}`}>{r.Excused}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AttendanceReportModal;
